import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type TouchEvent,
} from 'react';
import { humanizeError } from '@/api/errors';
import type { VideoReference } from '@/api/types';
import { track } from '@/analytics/tracker';
import { useLayer } from '@/app/layers/useLayer';
import { useToast } from '@/app/toasts/ToastProvider';
import { BookmarkIcon } from '@/components/icons/NavIcons';
import { appConfig, storageKeys } from '@/config/app';
import { mediaQuery } from '@/config/breakpoints';
import { FEATURES } from '@/features/catalog/filterModel';
import { useT } from '@/i18n/LocaleProvider';
import type { UiKey } from '@/i18n/dictionary';
import { useAddToCanvasPicker } from '@/library/AddToCanvasPicker';
import { useBookmarkMutations, useBookmarks } from '@/library/useBookmarks';
import { cx } from '@/lib/cx';
import { local } from '@/lib/storage';
import { formatDuration } from '@/lib/format';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { setDrawerInset } from './drawerInset';
import { embedAspect, stageAspect } from './embed';
import { EmbedPlayer } from './EmbedPlayer';
import { HostedPlayer } from './HostedPlayer';
import { LastFreeViewNote } from './LastFreeViewNote';
import { sanitizeSummaryHtml } from './sanitize';
import { hasTutorials, tutorialGroups, type TutorialItem } from './tutorials';
import { useVideo } from './useVideo';
import { useVideoDetail } from './VideoDetailContext';
import { bodyWheelDelta } from './wheelRedirect';
import styles from './VideoDrawer.module.css';

type SectionKey = 'glance' | 'breakdown' | 'technique' | 'learn';
const SECTIONS: SectionKey[] = ['glance', 'breakdown', 'technique', 'learn'];
const SWIPE_PX = 40;

/** Ширина панели: тянется за левый край, помнится между визитами (localStorage). */
const WIDTH_MIN_PX = 380;
const WIDTH_DEFAULT_PX = 520;
const WIDTH_MAX_RATIO = 0.92;
const WIDTH_KEY_STEP_PX = 24;

function clampWidth(px: number): number {
  const max = Math.max(WIDTH_MIN_PX, Math.round(window.innerWidth * WIDTH_MAX_RATIO));
  return Math.round(Math.max(WIDTH_MIN_PX, Math.min(px, max)));
}

function storedWidth(): number {
  const value = Number(local.get(storageKeys.videoDrawerWidth));
  return Number.isFinite(value) && value > 0 ? clampWidth(value) : WIDTH_DEFAULT_PX;
}

/** Высота кадра: тянется за границу под сценой, помнится между визитами. */
const STAGE_MIN_RATIO = 0.2;
const STAGE_MAX_RATIO = 0.72;

/** Табы внутри прокрутки (мобильный) прилипают к верху и закрывают начало секции. */
function stickyTabsHeight(body: HTMLElement, tabs: HTMLElement | null): number {
  return tabs && body.contains(tabs) ? tabs.offsetHeight : 0;
}

function storedStage(): number | null {
  const value = Number(local.get(storageKeys.videoStageHeight));
  return Number.isFinite(value) && value > 0 ? value : null;
}

const isTyping = (target: EventTarget | null) => {
  const el = target as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

/**
 * Панель разбора видео (VID-1…6, PLAY-1…6): справа 520px без затемнения,
 * сетка остаётся кликабельной, клик по другой карточке меняет содержимое.
 * Страницы с сеткой отодвигаются на ширину панели — карточки не уходят под неё.
 */
/** guest — публичный канвас: без Save и Add to canvas (CNV-24). */
export function VideoDrawer({ guest = false }: { guest?: boolean } = {}) {
  const t = useT();
  const detail = useVideoDetail();
  const { videoId, meta, source, canPrev, canNext, closeVideo, navigateVideo } = detail;
  const open = videoId !== null;
  const { data: video, isLoading, error, refetch } = useVideo(videoId);
  // Мобильный: панель во весь экран, а у сцены, шапки и табов нет своей прокрутки — без
  // блокировки свайп по ним листает каталог за панелью и подгружает его страницы.
  const isMobile = useMediaQuery(mediaQuery.mobile);
  const { isTop } = useLayer(open, closeVideo, {
    history: false,
    lockScroll: isMobile,
    closeOnEscape: true,
  });

  const drawerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const jumpsRef = useRef<HTMLDivElement>(null);
  const sectionRefs = useRef<Record<SectionKey, HTMLElement | null>>({
    glance: null,
    breakdown: null,
    technique: null,
    learn: null,
  });
  const [active, setActive] = useState<SectionKey>('glance');
  const [width, setWidth] = useState(storedWidth);
  const [dragging, setDragging] = useState(false);
  const widthRef = useRef(width);
  widthRef.current = width;
  // null — высота сцены по пропорциям кадра (значение по умолчанию).
  const [stageH, setStageH] = useState<number | null>(storedStage);
  const [splitting, setSplitting] = useState(false);
  const stageRef = useRef<HTMLDivElement>(null);
  const stageHRef = useRef(stageH);
  stageHRef.current = stageH;
  const [fallback, setFallback] = useState(false);
  const touch = useRef<{ x: number; y: number } | null>(null);
  // Клик по чипу секции: пока идёт плавная прокрутка, скролл-спай чип не перебивает.
  const jumpLock = useRef<number>(0);

  // Скроллбар страницы прячется, на десктопе страница продолжает скроллиться; фокус — в панель и обратно.
  useEffect(() => {
    if (!open) return;
    const previous = document.activeElement as HTMLElement | null;
    document.documentElement.setAttribute('data-hide-page-scrollbar', '');
    const raf = requestAnimationFrame(() => closeRef.current?.focus({ preventScroll: true }));
    return () => {
      cancelAnimationFrame(raf);
      document.documentElement.removeAttribute('data-hide-page-scrollbar');
      if (previous && document.contains(previous)) previous.focus({ preventScroll: true });
    };
  }, [open]);

  // Колесо над сценой, шапкой и табами листает тело панели. Своей прокрутки у этих частей
  // нет, и без перехвата браузер отдаёт колесо странице — уезжает каталог за панелью.
  // Слушатель нативный: React вешает wheel пассивным, preventDefault в нём не работает.
  useEffect(() => {
    const drawer = drawerRef.current;
    if (!open || !drawer) return;
    const onWheel = (event: WheelEvent) => {
      const body = bodyRef.current;
      if (body?.contains(event.target as Node)) return;
      const delta = bodyWheelDelta(event, body?.clientHeight ?? 0);
      if (delta === null) return;
      event.preventDefault();
      if (body) body.scrollTop += delta;
    };
    drawer.addEventListener('wheel', onWheel, { passive: false });
    return () => drawer.removeEventListener('wheel', onWheel);
  }, [open]);

  // Ширина панели — странице: сетка перекладывается в оставшееся место, в том же кадре.
  // Снятие — отдельным эффектом: иначе протяжка ручки сбрасывала бы отступ между значениями.
  useLayoutEffect(() => {
    if (open) setDrawerInset(width);
  }, [open, width]);
  useLayoutEffect(() => {
    if (!open) return;
    return () => setDrawerInset(null);
  }, [open]);

  // Смена видео: состояние панели сбрасывается, тело прокручивается наверх.
  useEffect(() => {
    setActive('glance');
    setFallback(false);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [videoId]);

  // Аналитика открытия — когда есть данные (VID-6).
  const trackedId = useRef<number | null>(null);
  useEffect(() => {
    if (!video || trackedId.current === video.id) return;
    trackedId.current = video.id;
    track('video_open', {
      metadata: {
        video_id: video.id,
        title: video.title,
        platform: video.platform,
        source,
        position: meta?.position !== undefined ? meta.position + 1 : undefined,
        search_event_id: meta?.searchEventId ?? undefined,
      },
    });
  }, [video, source, meta]);
  useEffect(() => {
    if (!open) trackedId.current = null;
  }, [open]);

  // ←/→ листают выдачу, когда панель — верхний слой и фокус не в поле ввода (VID-4).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (!isTop() || isTyping(e.target)) return;
      if (e.key === 'ArrowLeft') navigateVideo('prev');
      if (e.key === 'ArrowRight') navigateVideo('next');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, isTop, navigateVideo]);

  const onTouchStart = (e: TouchEvent) => {
    // Протяжка по полосе перемотки — не свайп между видео.
    if ((e.target as HTMLElement).closest('[data-no-swipe]')) {
      touch.current = null;
      return;
    }
    const t = e.touches[0];
    touch.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const onTouchEnd = (e: TouchEvent) => {
    const start = touch.current;
    const t = e.changedTouches[0];
    touch.current = null;
    if (!start || !t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) <= Math.abs(dy)) return;
    if (window.getSelection()?.toString()) return;
    navigateVideo(dx < 0 ? 'next' : 'prev');
  };

  const onBodyScroll = useCallback(() => {
    const body = bodyRef.current;
    if (!body) return;
    if (Date.now() < jumpLock.current) return;
    const top = body.scrollTop + stickyTabsHeight(body, jumpsRef.current) + 40;
    let current: SectionKey = 'glance';
    for (const key of SECTIONS) {
      const el = sectionRefs.current[key];
      if (el && el.offsetTop - body.offsetTop <= top) current = key;
    }
    setActive((prev) => (prev === current ? prev : current));
  }, []);

  const jumpTo = (key: SectionKey) => {
    const body = bodyRef.current;
    const el = sectionRefs.current[key];
    if (!body || !el) return;
    jumpLock.current = Date.now() + 800;
    body.scrollTo({
      top: el.offsetTop - body.offsetTop - stickyTabsHeight(body, jumpsRef.current) - 4,
      behavior: 'smooth',
    });
    setActive(key);
  };

  const applyWidth = (px: number) => {
    const next = clampWidth(px);
    setWidth(next);
    return next;
  };

  const persistWidth = () => local.set(storageKeys.videoDrawerWidth, String(widthRef.current));

  const onResizeDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
  };

  const onResizeMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    applyWidth(window.innerWidth - event.clientX);
  };

  const onResizeUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    setDragging(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    persistWidth();
  };

  const onResizeKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowLeft'
        ? WIDTH_KEY_STEP_PX
        : event.key === 'ArrowRight'
          ? -WIDTH_KEY_STEP_PX
          : 0;
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    widthRef.current = applyWidth(widthRef.current + step);
    persistWidth();
  };

  const clampStage = (px: number) => {
    const total = drawerRef.current?.clientHeight ?? window.innerHeight;
    return Math.round(Math.max(total * STAGE_MIN_RATIO, Math.min(px, total * STAGE_MAX_RATIO)));
  };

  const applyStage = (px: number) => {
    const next = clampStage(px);
    setStageH(next);
    stageHRef.current = next;
    return next;
  };

  const persistStage = () => {
    if (stageHRef.current === null) local.remove(storageKeys.videoStageHeight);
    else local.set(storageKeys.videoStageHeight, String(stageHRef.current));
  };

  const onSplitDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setSplitting(true);
  };

  const onSplitMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!splitting) return;
    const top = stageRef.current?.getBoundingClientRect().top ?? 0;
    applyStage(event.clientY - top);
  };

  const onSplitUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!splitting) return;
    setSplitting(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId))
      event.currentTarget.releasePointerCapture(event.pointerId);
    persistStage();
  };

  /** Двойной клик по границе — вернуть высоту по пропорциям кадра. */
  const onSplitReset = () => {
    setStageH(null);
    stageHRef.current = null;
    persistStage();
  };

  const onSplitKey = (event: ReactKeyboardEvent<HTMLDivElement>) => {
    const step =
      event.key === 'ArrowUp'
        ? -WIDTH_KEY_STEP_PX
        : event.key === 'ArrowDown'
          ? WIDTH_KEY_STEP_PX
          : 0;
    if (!step) return;
    event.preventDefault();
    event.stopPropagation();
    const current = stageHRef.current ?? stageRef.current?.getBoundingClientRect().height ?? 0;
    applyStage(current + step);
    persistStage();
  };

  if (!open) return null;

  return (
    <div
      className={styles.root}
      role="dialog"
      aria-modal="false"
      aria-label={video?.title ?? t('video.breakdownAria')}
    >
      <div
        ref={drawerRef}
        className={styles.drawer}
        style={{ '--drawer-w': `${width}px` } as CSSProperties}
        tabIndex={-1}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div
          className={styles.resizer}
          data-dragging={dragging ? 'true' : 'false'}
          role="separator"
          aria-orientation="vertical"
          title={t('video.resizeHint')}
          aria-label={t('video.resize')}
          aria-valuenow={width}
          aria-valuemin={WIDTH_MIN_PX}
          tabIndex={0}
          onPointerDown={onResizeDown}
          onPointerMove={onResizeMove}
          onPointerUp={onResizeUp}
          onPointerCancel={onResizeUp}
          onKeyDown={onResizeKey}
        />
        <div
          ref={stageRef}
          className={styles.stage}
          style={
            video
              ? ({
                  ...(stageH !== null ? { height: `${stageH}px`, maxHeight: 'none' } : null),
                  // Сцена строится по тому, что реально покажет плеер: свой файл — по
                  // размерам файла, embed — по кадрированию площадки.
                  '--stage-ar': video.media && !fallback ? stageAspect(video) : embedAspect(video),
                } as CSSProperties)
              : undefined
          }
        >
          {video ? (
            video.media && !fallback ? (
              <HostedPlayer
                key={video.id}
                video={video}
                media={video.media}
                onFallback={() => setFallback(true)}
              />
            ) : (
              <EmbedPlayer key={video.id} video={video} />
            )
          ) : null}
          <button
            ref={closeRef}
            type="button"
            className={cx(styles.stageButton, styles.close)}
            title={t('video.closeEsc')}
            aria-label={t('common.close')}
            onClick={closeVideo}
          >
            ×
          </button>
          <div className={styles.nav}>
            <button
              type="button"
              className={styles.stageButton}
              title={t('video.previousKey')}
              aria-label={t('video.previous')}
              disabled={!canPrev}
              onClick={() => navigateVideo('prev')}
            >
              ‹
            </button>
            <button
              type="button"
              className={styles.stageButton}
              title={t('video.nextKey')}
              aria-label={t('video.next')}
              disabled={!canNext}
              onClick={() => navigateVideo('next')}
            >
              ›
            </button>
          </div>
        </div>

        <div
          className={styles.splitter}
          data-dragging={splitting ? 'true' : 'false'}
          role="separator"
          aria-orientation="horizontal"
          title={t('video.resizeStageHint')}
          aria-label={t('video.resizeStage')}
          tabIndex={0}
          onPointerDown={onSplitDown}
          onPointerMove={onSplitMove}
          onPointerUp={onSplitUp}
          onPointerCancel={onSplitUp}
          onDoubleClick={onSplitReset}
          onKeyDown={onSplitKey}
        />

        {video ? (
          <VideoBody
            video={video}
            active={active}
            bodyRef={bodyRef}
            jumpsRef={jumpsRef}
            inline={isMobile}
            sectionRefs={sectionRefs}
            onBodyScroll={onBodyScroll}
            onJump={jumpTo}
            searchEventId={meta?.searchEventId ?? null}
            guest={guest}
          />
        ) : isLoading ? (
          <div className={styles.state}>
            <span className={styles.stateLabel}>{t('common.loading')}</span>
          </div>
        ) : (
          <div className={styles.state} role="alert">
            {humanizeError(error)}
            <div>
              <button type="button" className={styles.retry} onClick={() => void refetch()}>
                {t('common.tryAgain')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface BodyProps {
  video: VideoReference;
  active: SectionKey;
  bodyRef: React.RefObject<HTMLDivElement>;
  jumpsRef: React.RefObject<HTMLDivElement>;
  /** Шапка и табы внутри прокручиваемого тела (мобильный). */
  inline: boolean;
  sectionRefs: React.MutableRefObject<Record<SectionKey, HTMLElement | null>>;
  onBodyScroll: () => void;
  onJump: (key: SectionKey) => void;
  searchEventId: number | null;
  guest: boolean;
}

function VideoBody({
  video,
  active,
  bodyRef,
  jumpsRef,
  inline,
  sectionRefs,
  onBodyScroll,
  onJump,
  searchEventId,
  guest,
}: BodyProps) {
  const t = useT();
  const toast = useToast();
  const { data: bookmarks } = useBookmarks();
  const { setBookmark } = useBookmarkMutations();
  const { openPicker } = useAddToCanvasPicker();
  const saved = bookmarks?.has(video.id) ?? false;

  const summaryHtml = useMemo(
    () => (video.public_summary_html ? sanitizeSummaryHtml(video.public_summary_html) : null),
    [video.public_summary_html],
  );
  const features = FEATURES.filter((f) => video[f.key]).map((f) =>
    t(`values.features.${f.key}` as UiKey),
  );
  const tutorials = tutorialGroups(video.tutorials);
  // tutorial_label — подпись платформы для редактора и название для произвольной ссылки (как в событиях раньше).
  const trackTutorial = (code: string, label: string) =>
    track('tutorial_link_clicked', {
      metadata: { video_id: video.id, tutorial_code: code, tutorial_label: label },
    });
  const facts: Array<[string, string]> = [
    [t('video.pacing'), video.pacing ? t(`values.pacing.${video.pacing}` as UiKey) : '—'],
    [
      t('video.production'),
      video.production_level ? t(`values.level.${video.production_level}` as UiKey) : '—',
    ],
    [t('video.hook'), video.hook?.name ?? '—'],
    [
      t('video.orientation'),
      video.orientation ? t(`values.orientation.${video.orientation}` as UiKey) : '—',
    ],
    [t('video.duration'), formatDuration(video.duration_sec)],
    [t('video.source'), video.media ? t('video.sourceHosted') : t('video.sourceEmbed')],
  ];
  const jumps: Array<[SectionKey, string]> = [
    ['glance', t('video.glance')],
    ['breakdown', t('video.breakdown')],
    ['technique', t('video.technique')],
    ['learn', t('video.learn')],
  ];

  const copyLink = async () => {
    const link = `${window.location.origin}${appConfig.basePath}?video=${video.id}`;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // буфер недоступен — ссылка остаётся в адресной строке
      toast.show(t('video.linkCopyFailed'));
      return;
    }
    toast.show(t('video.linkCopied'));
    track('video_link_copied', { metadata: { video_id: video.id, platform: video.platform } });
  };

  const head = (
    <div className={styles.head}>
      {video.categories.length ? (
        <div className={styles.cats}>
          {video.categories.map((c) => (
            <span key={c.id} className={styles.cat}>
              {c.name}
            </span>
          ))}
        </div>
      ) : null}
      <h2 className={styles.title}>{video.title}</h2>
      <div className={styles.actions}>
        {guest ? null : (
          <>
            {/* Кнопка одна и та же на всех видео: подпись не меняется, состояние —
                  aria-pressed и заливка закладки (как ленточка на карточке каталога). */}
            <button
              type="button"
              className={styles.save}
              aria-pressed={saved}
              title={saved ? t('video.savedHint') : undefined}
              onClick={() => setBookmark(video.id, !saved, { searchEventId })}
            >
              <BookmarkIcon className={styles.saveIcon} fill={saved ? 'currentColor' : 'none'} />
              {t('catalog.save')}
            </button>
            <button
              type="button"
              className={styles.action}
              onClick={() => openPicker([video.id], 'detail', searchEventId)}
            >
              <span className={styles.dashed} aria-hidden="true" />
              {t('catalog.addToCanvas')}
            </button>
          </>
        )}
        <a
          className={styles.action}
          href={video.source_url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() =>
            track('video_external_click', {
              metadata: {
                video_id: video.id,
                source_url: video.source_url,
                platform: video.platform,
                search_event_id: searchEventId ?? undefined,
              },
            })
          }
        >
          {t('video.original')}
        </a>
        <button
          type="button"
          className={styles.copy}
          title={t('video.copyLink')}
          aria-label={t('video.copyLink')}
          onClick={copyLink}
        >
          {t('video.link')}
        </button>
      </div>
    </div>
  );
  const tabs = (
    <div ref={jumpsRef} className={styles.jumps} role="tablist" aria-label={t('video.sections')}>
      {jumps.map(([key, label]) => (
        <button
          key={key}
          type="button"
          role="tab"
          aria-selected={active === key}
          className={cx(styles.jump, active === key && styles.jumpActive)}
          onClick={() => onJump(key)}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // Мобильный: шапка и табы — внутри прокрутки. Название и кнопки уезжают вверх,
  // табы прилипают — под разбор остаётся всё, что ниже кадра.
  return (
    <>
      {inline ? null : head}
      {inline ? null : tabs}
      <div
        ref={bodyRef}
        className={cx(styles.body, inline && styles.bodyInline)}
        onScroll={onBodyScroll}
      >
        {inline ? head : null}
        {inline ? tabs : null}
        {/* Первая строка прокручиваемого тела: видна при открытии и не отнимает высоту
            у разбора — на 360px шапка и сцена и так съедают почти весь экран. */}
        {guest ? null : <LastFreeViewNote videoId={video.id} />}
        <section ref={(el) => (sectionRefs.current.glance = el)} className={styles.section}>
          <p className={styles.eyebrow}>01 · {t('video.glance')}</p>
          <div className={styles.facts}>
            {facts.map(([key, value]) => (
              <div key={key} className={styles.fact}>
                <div className={styles.factKey}>{key}</div>
                <div className={styles.factValue}>{value}</div>
              </div>
            ))}
          </div>
        </section>

        <section ref={(el) => (sectionRefs.current.breakdown = el)} className={styles.section}>
          <p className={styles.eyebrow}>02 · {t('video.breakdown')}</p>
          {summaryHtml ? (
            <div className={styles.summary} dangerouslySetInnerHTML={{ __html: summaryHtml }} />
          ) : video.public_summary ? (
            <p className={cx(styles.summary, styles.plain)}>{video.public_summary}</p>
          ) : (
            <p className={styles.none}>—</p>
          )}
          {video.details_public ? (
            <>
              <p className={styles.detailsEyebrow}>{t('video.additionalDetails')}</p>
              <p className={styles.details}>{video.details_public}</p>
            </>
          ) : null}
        </section>

        <section ref={(el) => (sectionRefs.current.technique = el)} className={styles.section}>
          <p className={styles.eyebrow}>03 · {t('video.technique')}</p>
          <div className={styles.tech}>
            <span className={styles.techLabel}>{t('video.effects')}</span>
            <div className={styles.chips}>
              {video.transition_types.length ? (
                video.transition_types.map((e) => (
                  <span key={e.id} className={styles.chipFill}>
                    {e.name}
                  </span>
                ))
              ) : (
                <span className={styles.none}>—</span>
              )}
            </div>
            <span className={styles.techLabel}>{t('video.features')}</span>
            <div className={styles.chips}>
              {features.length ? (
                features.map((f) => (
                  <span key={f} className={styles.chipLine}>
                    {f}
                  </span>
                ))
              ) : (
                <span className={styles.none}>—</span>
              )}
            </div>
          </div>
        </section>

        <section
          ref={(el) => (sectionRefs.current.learn = el)}
          className={cx(styles.section, styles.sectionLast)}
        >
          <p className={styles.eyebrow}>04 · {t('video.learn')}</p>
          {hasTutorials(tutorials) ? (
            <div className={styles.tutorials}>
              {tutorials.editors.map((group) => (
                <TutorialGroup
                  key={group.code}
                  badge={group.code}
                  label={group.label}
                  items={group.items}
                  onOpen={() => trackTutorial(group.code, group.label)}
                />
              ))}
              {tutorials.other.length ? (
                <TutorialGroup
                  badge="↗"
                  label={t('video.tutorialsOther')}
                  items={tutorials.other}
                  onOpen={(item) => trackTutorial('CUSTOM', item.title ?? '')}
                />
              ) : null}
            </div>
          ) : (
            <p className={styles.empty}>{t('video.noTutorials')}</p>
          )}
        </section>
      </div>
    </>
  );
}

/**
 * Раскрывающийся блок раздела Learn: редактор (или Other) и его туториалы. Нативный
 * <details> — открывается тапом, с клавиатуры и читалкой экрана без своего состояния.
 */
function TutorialGroup({
  badge,
  label,
  items,
  onOpen,
}: {
  badge: string;
  label: string;
  items: TutorialItem[];
  onOpen: (item: TutorialItem) => void;
}) {
  const t = useT();
  return (
    <details className={styles.tutorialGroup}>
      <summary className={styles.tutorialSummary}>
        <span className={styles.tutorialLeft}>
          <span className={styles.badge} aria-hidden="true">
            {badge}
          </span>
          {label}
        </span>
        <span className={styles.tutorialCount}>{items.length}</span>
        <span className={styles.tutorialChevron} aria-hidden="true">
          ▾
        </span>
      </summary>
      {items.length ? (
        <div className={styles.tutorialList}>
          {items.map((item, index) => (
            <a
              key={`${index}-${item.url}`}
              className={styles.tutorialLink}
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onOpen(item)}
            >
              <span className={styles.tutorialTitle}>
                {item.title ?? t('video.tutorialUntitled', { n: index + 1 })}
              </span>
              <span className={styles.tutorialMeta}>{t('video.tutorialMeta')}</span>
            </a>
          ))}
        </div>
      ) : (
        <p className={styles.tutorialEmpty}>{t('video.tutorialsEmpty')}</p>
      )}
    </details>
  );
}
